import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { ProgressBar } from 'primereact/progressbar';
import GenericFileUpload from '../../../../../../../components/common/GenericFileUpload';
import { useGenerateVideoUploadUrlMutation, useCompleteVideoUploadMutation, useDeleteWarrantyClaimVideoMutation } from '../../../../../../../api/endpoints/warrantyClaims';
import toastService from '../../../../../../../utils/toastService';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';

const MAX_FILE_SIZE = 500 * 1024 * 1024; // 500MB
const ACCEPTED_EXTENSIONS = '.mp4, .avi, .mov, .webm';
const VIDEO_ACCEPTED_FILE_TYPES = {
    'video/mp4': ['.mp4'],
    'video/x-msvideo': ['.avi'],
    'video/quicktime': ['.mov'],
    'video/webm': ['.webm']
};

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const uploadToS3WithProgress = ({ uploadUrl, file, contentType, onProgress }) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', contentType);

        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            const percent = Math.round((event.loaded * 100) / event.total);
            onProgress?.(percent);
        };

        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
                return;
            }
            reject(new Error(xhr.responseText || `S3 upload failed: ${xhr.status}`));
        };

        xhr.onerror = () => reject(new Error('S3 upload network error'));
        xhr.send(file);
    });

const AttachedVideosFormDialog = ({ disabledStatuses, claimId, dialogVisible, hideDialog, editingVideo, onSuccess }) => {
    const { th } = useLanguage();

    const [formData, setFormData] = useState({ title: '', description: '' });
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStep, setUploadStep] = useState(null); // 'generating' | 'uploading' | 'completing'

    const [generateUploadUrl] = useGenerateVideoUploadUrlMutation();
    const [completeUpload] = useCompleteVideoUploadMutation();
    const [deleteVideo] = useDeleteWarrantyClaimVideoMutation();

    useEffect(() => {
        if (dialogVisible) {
            if (editingVideo) {
                setFormData({
                    title: editingVideo.title || '',
                    description: editingVideo.description || ''
                });
            } else {
                resetForm();
            }
        } else {
            resetForm();
        }
    }, [dialogVisible, editingVideo]);

    const resetForm = () => {
        setFormData({ title: '', description: '' });
        setSelectedFile(null);
        setErrors({});
        setIsSubmitting(false);
        setUploadProgress(0);
        setUploadStep(null);
    };

    const handleDialogClose = () => {
        resetForm();
        hideDialog();
    };

    const handleVideoFilesChange = (files) => {
        const file = files?.[0] || null;
        setSelectedFile(file);
        setErrors((prev) => ({ ...prev, file: null }));

        if (file && !formData.title) {
            setFormData((prev) => ({ ...prev, title: file.name.replace(/\.[^/.]+$/, '') }));
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!editingVideo && !selectedFile) {
            newErrors.file = 'Video dosyası seçilmesi zorunludur.';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            if (selectedFile) {
                // Adım 1: Pre-signed URL al
                setUploadStep('generating');
                const generateResult = await generateUploadUrl({
                    claimId,
                    fileName: selectedFile.name,
                    fileSize: selectedFile.size,
                    title: formData.title || selectedFile.name,
                    description: formData.description,
                    expirationMinutes: 30
                }).unwrap();

                // API wrapper'lı (data içinde) veya direkt payload dönüşlerini destekle
                const generatePayload = generateResult?.data ?? generateResult;
                const { uploadUrl, contentType, videoId } = generatePayload || {};

                if (!uploadUrl || !videoId) {
                    throw new Error(generateResult?.errorMessage || generateResult?.message || 'Upload URL alınamadı.');
                }
                const resolvedContentType = contentType || selectedFile.type || 'video/mp4';

                // Adım 2: Doğrudan S3'e yükle (auth header olmadan)
                setUploadStep('uploading');
                setUploadProgress(0);
                await uploadToS3WithProgress({
                    uploadUrl,
                    file: selectedFile,
                    contentType: resolvedContentType,
                    onProgress: (percent) => {
                        setUploadProgress(percent);
                    }
                });

                // Adım 3: Yüklemeyi tamamla
                setUploadStep('completing');
                const completeResult = await completeUpload({
                    claimId,
                    videoId,
                    durationSeconds: undefined
                }).unwrap();

                // Bazı ortamlarda complete-upload 204/boş body dönebilir.
                // explicit false olmadığı sürece başarılı kabul ediyoruz.
                if (completeResult?.isSuccessful === false) {
                    throw new Error(completeResult?.errorMessage || completeResult?.message || 'Yükleme tamamlanamadı.');
                }

                toastService.success('Video başarıyla yüklendi.');
            }

            if (onSuccess) onSuccess();
            resetForm();
            hideDialog();
        } catch (error) {
            console.error('Video yükleme hatası:', error);
            const msg = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
            toastService.error(`Video yüklenemedi: ${msg}`);
        } finally {
            setIsSubmitting(false);
            setUploadStep(null);
            setUploadProgress(0);
        }
    };

    const handleDelete = async () => {
        if (!editingVideo?.id) return;
        if (!window.confirm(`"${editingVideo.fileName || editingVideo.title}" videosunu silmek istediğinizden emin misiniz?`)) return;

        setIsSubmitting(true);
        try {
            const result = await deleteVideo({ claimId, videoId: editingVideo.id, hardDelete: false }).unwrap();
            if (result?.isSuccessful !== false) {
                toastService.success('Video başarıyla silindi.');
                if (onSuccess) onSuccess();
                resetForm();
                hideDialog();
            }
        } catch (error) {
            const msg = error?.data?.message || error?.message || 'Bilinmeyen bir hata oluştu.';
            toastService.error(`Video silinemedi: ${msg}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const getUploadStepLabel = () => {
        switch (uploadStep) {
            case 'generating': return 'Yükleme URL\'si alınıyor...';
            case 'uploading': return `S3'e yükleniyor... %${uploadProgress}`;
            case 'completing': return 'Yükleme tamamlanıyor...';
            default: return '';
        }
    };

    const footer = (
        <div className="flex justify-content-between align-items-center">
            {editingVideo && (
                <Button
                    label="Sil"
                    icon="pi pi-trash"
                    className="p-button-danger"
                    onClick={handleDelete}
                    disabled={isSubmitting || disabledStatuses}
                />
            )}
            <div className="flex gap-2 justify-content-end w-full">
                <Button label="İptal" icon="pi pi-times" onClick={handleDialogClose} outlined disabled={isSubmitting} />
                {!editingVideo && (
                    <Button
                        label="Yükle"
                        icon="pi pi-upload"
                        onClick={handleSave}
                        disabled={isSubmitting || disabledStatuses || !selectedFile}
                        loading={isSubmitting}
                    />
                )}
            </div>
        </div>
    );

    return (
        <Dialog
            visible={dialogVisible}
            style={{ width: '50rem' }}
            header={editingVideo ? 'Video Detayı' : 'Video Ekle'}
            modal
            className="p-fluid"
            footer={footer}
            onHide={handleDialogClose}
            closable={!isSubmitting}
        >
            <div className="field mb-3">
                <label htmlFor="video-title">Başlık</label>
                <InputText
                    id="video-title"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    placeholder="Video başlığı (isteğe bağlı)"
                    disabled={isSubmitting || disabledStatuses || !!editingVideo}
                />
            </div>

            <div className="field mb-3">
                <label htmlFor="video-description">Açıklama</label>
                <InputText
                    id="video-description"
                    value={formData.description}
                    onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="Açıklama (isteğe bağlı)"
                    disabled={isSubmitting || disabledStatuses || !!editingVideo}
                />
            </div>

            {!editingVideo && (
                <div className="field mb-3">
                    <label htmlFor="video-file">
                        Video Dosyası <span className="p-error">*</span>
                    </label>
                    <GenericFileUpload
                        newFiles={!disabledStatuses ? (selectedFile ? [selectedFile] : []) : []}
                        onFilesChange={!disabledStatuses ? handleVideoFilesChange : () => {}}
                        existingDocuments={[]}
                        isLoading={isSubmitting}
                        disabled={disabledStatuses}
                        multiple={false}
                        maxFileSize={MAX_FILE_SIZE}
                        acceptedFileTypes={VIDEO_ACCEPTED_FILE_TYPES}
                        showDownloadButton={false}
                        showUpdateButton={false}
                        showDeleteButton={true}
                        readOnlyMode={disabledStatuses}
                    />
                    <small className="text-color-secondary">Kabul edilen formatlar: {ACCEPTED_EXTENSIONS} — Maksimum boyut: {formatFileSize(MAX_FILE_SIZE)}</small>
                    {errors.file && <small className="p-error">{errors.file}</small>}
                </div>
            )}

            {editingVideo && (
                <div className="field mb-3">
                    <label>Dosya Bilgisi</label>
                    <div className="flex flex-column gap-1 p-3 border-round surface-100">
                        <div className="flex gap-2 align-items-center">
                            <i className="pi pi-video text-primary" />
                            <span className="font-semibold text-sm">{editingVideo.fileName}</span>
                        </div>
                        {editingVideo.fileSize && (
                            <span className="text-color-secondary text-sm ml-4">{formatFileSize(editingVideo.fileSize)}</span>
                        )}
                        {editingVideo.durationSeconds && (
                            <span className="text-color-secondary text-sm ml-4">
                                Süre: {Math.floor(editingVideo.durationSeconds / 60)}:{String(editingVideo.durationSeconds % 60).padStart(2, '0')}
                            </span>
                        )}
                        {editingVideo.contentType && (
                            <span className="text-color-secondary text-sm ml-4">Tür: {editingVideo.contentType}</span>
                        )}
                    </div>
                </div>
            )}

            {isSubmitting && uploadStep && (
                <div className="mt-3">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-spin pi-spinner text-primary" />
                        <span className="text-sm font-semibold">{getUploadStepLabel()}</span>
                    </div>
                    {uploadStep === 'uploading' && (
                        <ProgressBar value={uploadProgress} showValue displayValueTemplate={(v) => `${v}%`} style={{ height: '1.2rem' }} />
                    )}
                </div>
            )}
        </Dialog>
    );
};

export default AttachedVideosFormDialog;
